import { useTranslation } from 'react-i18next';

import { frameworkLabel, useFramework } from '../framework';
import { SubdomainRow } from './SubdomainRow';
import './FrameworkPreview.css';

/**
 * Phase 1 first cut — the framework preview surface.
 *
 * Renders the 17 subdomains grouped by domain in six cards. Each row carries
 * a working StatePicker (local state only — no save yet) so kaimahi can
 * experience the editor interaction model in advance of the data layer.
 *
 * Sits below the Phase 0 placeholder welcome on the kaimahi home until
 * Phase 1 routing lands and gives this its own page. See ADR-005 for the
 * data model and ADR-006 for the access control rules that will gate this
 * surface once whānau data is real.
 */
export function FrameworkPreview() {
  const { t, i18n } = useTranslation();
  const { domains, subdomainsForDomain } = useFramework();

  return (
    <section
      className="framework-preview"
      aria-labelledby="framework-preview-title"
    >
      <header className="framework-preview__header">
        <h2
          id="framework-preview-title"
          className="framework-preview__title"
        >
          {t('preview.title')}
        </h2>
        <p className="framework-preview__subtitle">{t('preview.subtitle')}</p>
      </header>

      <div className="framework-preview__domains">
        {domains.map((domain) => {
          const subdomains = subdomainsForDomain(domain.id);
          return (
            <article
              key={domain.id}
              className="domain-card"
              aria-labelledby={`domain-heading-${domain.id}`}
            >
              <h3
                id={`domain-heading-${domain.id}`}
                className="domain-card__heading"
              >
                <span className="domain-card__heading-text">
                  {frameworkLabel(domain, 'domain', i18n.language, t)}
                </span>
                <span
                  className="domain-card__count"
                  aria-label={t('preview.subdomainCount', {
                    count: subdomains.length,
                  })}
                >
                  {subdomains.length}
                </span>
              </h3>

              <ul className="domain-card__subdomains">
                {subdomains.map((sub) => (
                  <SubdomainRow key={sub.id} subdomain={sub} />
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default FrameworkPreview;
